import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Item, Order, Profile } from '@lr/shared';
import { apiFetch } from '../../lib/api';

interface Stats {
  itemsTotal: number;
  itemsAvailable: number;
  itemsDraft: number;
  ordersAwaitingId: number;
  ordersToShip: number;
  ordersOut: number;
  ordersOverdue: number;
  customers: number;
}

export function AdminDashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void Promise.all([
      apiFetch<Item[]>('/api/admin/items'),
      apiFetch<(Order & { user?: { id_status?: string } | null })[]>(
        '/api/admin/orders'
      ),
      apiFetch<Profile[]>('/api/admin/users')
    ])
      .then(([items, orders, users]) => {
        const now = Date.now();
        const isRental = (o: Order) =>
          !o.bundle_purchased_id || o.bundle_purchased_id == null;
        const awaitingId = orders.filter(
          (o) =>
            isRental(o) &&
            (o.user?.id_status ?? 'not_submitted') !== 'approved' &&
            ['confirmed', 'preparing'].includes(o.status)
        ).length;
        const toShip = orders.filter(
          (o) =>
            isRental(o) &&
            ['confirmed', 'preparing'].includes(o.status) &&
            (o.user?.id_status ?? 'not_submitted') === 'approved'
        ).length;
        const out = orders.filter((o) =>
          ['shipped', 'delivered'].includes(o.status)
        ).length;
        const overdue = orders.filter((o) => {
          if (!o.return_due_date) return false;
          if (!['shipped', 'delivered'].includes(o.status)) return false;
          return new Date(o.return_due_date).getTime() < now;
        }).length;

        setStats({
          itemsTotal: items.length,
          itemsAvailable: items.filter((i) => i.status === 'available').length,
          itemsDraft: items.filter((i) => i.status === 'draft').length,
          ordersAwaitingId: awaitingId,
          ordersToShip: toShip,
          ordersOut: out,
          ordersOverdue: overdue,
          customers: users.filter((u) => u.role === 'user').length
        });
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <div className="admin-page-head">
        <div>
          <h1>Dashboard</h1>
          <div className="sub">
            What needs your attention right now. Manage individual orders in
            the Orders tab.
          </div>
        </div>
        <div className="actions">
          <Link to="items/new" className="btn-primary">
            + Add item
          </Link>
        </div>
      </div>

      {loading && (
        <div style={{ color: 'var(--a-ink-soft)', padding: 16 }}>Loading metrics…</div>
      )}

      {stats && (
        <>
          <div className="kpi-grid">
            <Link
              to="orders"
              className="kpi"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div className="lbl">Waiting on ID</div>
              <div
                className="val"
                style={{ color: stats.ordersAwaitingId > 0 ? 'var(--a-warn, #a85f00)' : undefined }}
              >
                {stats.ordersAwaitingId}
              </div>
              <div className="sub">orders that can&rsquo;t ship yet</div>
            </Link>
            <Link
              to="orders"
              className="kpi"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div className="lbl">Ready to ship</div>
              <div className="val">{stats.ordersToShip}</div>
              <div className="sub">ID approved, awaiting dispatch</div>
            </Link>
            <Link
              to="orders"
              className="kpi"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div className="lbl">Out on rental</div>
              <div className="val">{stats.ordersOut}</div>
              <div className="sub">shipped or delivered</div>
            </Link>
            <Link
              to="orders"
              className="kpi"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div className="lbl">Overdue</div>
              <div
                className="val"
                style={{
                  color: stats.ordersOverdue > 0 ? 'var(--a-danger, #b3261e)' : undefined
                }}
              >
                {stats.ordersOverdue}
              </div>
              <div className="sub">past 30-day return</div>
            </Link>
          </div>

          <div className="kpi-grid">
            <div className="kpi">
              <div className="lbl">Items live</div>
              <div className="val">{stats.itemsAvailable}</div>
              <div className="sub">{stats.itemsTotal} total · {stats.itemsDraft} draft</div>
            </div>
            <div className="kpi">
              <div className="lbl">Customers</div>
              <div className="val">{stats.customers}</div>
            </div>
            <div className="kpi">
              <div className="lbl">Today</div>
              <div className="val">{new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export function StatusBadge({ status }: { status: string }) {
  // Item & order statuses share this badge — keep colours distinct so
  // "rented out" doesn't look like "archived" at a glance.
  const map: Record<string, string> = {
    // Item statuses
    available: 'badge-good',
    rented_out: 'badge-info',
    reserved: 'badge-warn',
    archived: 'badge-neutral',
    draft: 'badge-neutral',
    coming_soon: 'badge-info',
    // Order statuses
    confirmed: 'badge-info',
    preparing: 'badge-info',
    shipped: 'badge-info',
    delivered: 'badge-good',
    returning: 'badge-warn',
    return_label_sent: 'badge-info',
    returned: 'badge-neutral',
    late: 'badge-warn',
    cancelled: 'badge-danger',
    pending_payment: 'badge-warn'
  };
  return <span className={`badge ${map[status] ?? 'badge-neutral'}`}>{status.replace(/_/g, ' ')}</span>;
}
